import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { 
  getApplication, 
  ApplicationRecord, 
  APPLICATION_STATUS_LABELS,
  getStatusHistory,
  StatusHistoryRecord,
  getDocumentRequests,
  getReportDocumentRequests,
  DocumentRequestRecord,
  DOCUMENT_REQUEST_STATUS_LABELS,
  ApplicationStatus,
  getApplicationReport,
  ReportRecord,
  REPORT_STATUS_LABELS,
  REPORT_DOCUMENT_LABELS,
  ReportStatus,
  ReportDocumentType,
  fulfillDocumentRequest,
  getRejectionReasonLabel,
  canUserCancelApplication,
  cancelApplication,
} from "@/lib/api/applications";

// Application phase statuses - document requests for application should be shown during these
const APPLICATION_PHASE_STATUSES: ApplicationStatus[] = [
  "draft",
  "submitted", 
  "under_review",
  "documents_requested",
  "approved",  // Still in application phase (before order)
];
import { directus } from "@/lib/directus";
import { uploadFiles, createItem } from "@directus/sdk";
import { cn } from "@/lib/utils";
import { RECIPIENT_TYPE_LABELS, EQUIPMENT_LABELS, DOCUMENT_LABELS, OBLIGATION_ITEMS, calculateAssistance, formatMoney as formatMoneyLib, EquipmentType, DocumentType, BOILER_ROOM_LABELS } from "@/lib/application";

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
    </svg>
  );
}

function getStatusColor(status: string): string {
  switch (status) {
    case "draft": return "bg-slate-100 text-slate-700";
    case "submitted": return "bg-blue-100 text-blue-700";
    case "under_review": return "bg-amber-100 text-amber-700";
    case "documents_requested": return "bg-orange-100 text-orange-700";
    case "approved": return "bg-green-100 text-green-700";
    case "rejected": return "bg-red-100 text-red-700";
    case "order_issued": return "bg-indigo-100 text-indigo-700";
    case "payment_pending": return "bg-violet-100 text-violet-700";
    case "payment_sent": return "bg-cyan-100 text-cyan-700";
    case "payment_received": return "bg-teal-100 text-teal-700";
    case "in_progress": return "bg-lime-100 text-lime-700";
    case "completed": return "bg-emerald-100 text-emerald-700";
    case "cancelled": return "bg-gray-100 text-gray-700";
    default: return "bg-slate-100 text-slate-700";
  }
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("uk-UA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("uk-UA", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(amount: number | null | undefined): string {
  if (!amount) return "—";
  return new Intl.NumberFormat("uk-UA", {
    style: "currency",
    currency: "UAH",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Full status history
function getFullStatusHistory(app: ApplicationRecord) {
  const history: { 
    status: string; 
    label: string; 
    date: string | null; 
    icon: "check" | "clock" | "error" | "edit"; 
    active: boolean;
    description?: string;
  }[] = [];
  
  // Draft created
  history.push({
    status: "draft_created",
    label: "Чернетку створено",
    date: app.date_created,
    icon: "check",
    active: true,
    description: "Заявку створено в системі",
  });
  
  // Draft updated (if different from created)
  if (app.date_updated && app.date_created && 
      new Date(app.date_updated).getTime() > new Date(app.date_created).getTime() + 60000) {
    history.push({
      status: "draft_updated",
      label: "Чернетку оновлено",
      date: app.date_updated,
      icon: "edit",
      active: true,
      description: "Останні зміни в заявці",
    });
  }
  
  // Submitted
  if (app.submitted_at) {
    history.push({
      status: "submitted",
      label: "Заявку подано",
      date: app.submitted_at,
      icon: "check",
      active: true,
      description: "Заявку подано на розгляд",
    });
  } else if (app.status !== "draft") {
    history.push({
      status: "submitted",
      label: "Очікує подання",
      date: null,
      icon: "clock",
      active: false,
    });
  }
  
  // Under review
  if (app.reviewed_at) {
    history.push({
      status: "under_review",
      label: "Розглянуто комісією",
      date: app.reviewed_at,
      icon: "check",
      active: true,
      description: app.reviewed_by ? `Розглянуто: ${app.reviewed_by}` : undefined,
    });
  } else if (["under_review", "approved", "rejected", "payment_received", "in_progress", "completed"].includes(app.status)) {
    history.push({
      status: "under_review",
      label: "На розгляді комісії",
      date: null,
      icon: "clock",
      active: false,
    });
  }
  
  // Decision
  if (app.status === "approved" || app.decision_type === "approved") {
    history.push({
      status: "approved",
      label: "Заявку схвалено",
      date: app.decision_date,
      icon: "check",
      active: true,
      description: app.decision_number ? `Рішення №${app.decision_number}` : undefined,
    });
  } else if (app.status === "rejected" || app.decision_type === "rejected") {
    history.push({
      status: "rejected",
      label: "Заявку відхилено",
      date: app.decision_date,
      icon: "error",
      active: true,
      description: app.rejection_reason ? getRejectionReasonLabel(app.rejection_reason) : undefined,
    });
  }
  
  // Order issued
  if (app.order_number) {
    history.push({
      status: "order_issued",
      label: "Наказ видано",
      date: app.order_date,
      icon: "check",
      active: true,
      description: `Наказ №${app.order_number}`,
    });
  }
  
  // Payment sent
  if (app.payment_date && !app.payment_received_at) {
    history.push({
      status: "payment_sent",
      label: "Кошти перераховано",
      date: app.payment_date,
      icon: "check",
      active: true,
      description: app.payment_amount ? `Сума: ${formatMoney(app.payment_amount)}` : undefined,
    });
  }
  
  // Payment received
  if (app.payment_received_at) {
    history.push({
      status: "payment_received",
      label: "Кошти отримано",
      date: app.payment_received_at,
      icon: "check",
      active: true,
      description: app.payment_amount ? `Сума: ${formatMoney(app.payment_amount)}` : undefined,
    });
  }
  
  // In progress
  if (app.status === "in_progress") {
    history.push({
      status: "in_progress",
      label: "Виконується",
      date: null,
      icon: "clock",
      active: true,
      description: "Реалізація проєкту в процесі",
    });
  }
  
  // Completed
  if (app.status === "completed") {
    history.push({
      status: "completed",
      label: "Проєкт завершено",
      date: app.date_updated,
      icon: "check",
      active: true,
      description: "Проєкт успішно реалізовано",
    });
  }
  
  return history;
}

export function ApplicationView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<ApplicationRecord | null>(null);
  const [statusHistory, setStatusHistory] = useState<StatusHistoryRecord[]>([]);
  const [documentRequests, setDocumentRequests] = useState<DocumentRequestRecord[]>([]);  // Application-phase requests
  const [reportDocumentRequests, setReportDocumentRequests] = useState<DocumentRequestRecord[]>([]);  // Report-phase requests
  const [report, setReport] = useState<ReportRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "details" | "report">("overview");
  const [uploadingRequestId, setUploadingRequestId] = useState<number | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  
  // Cancel dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Handle application cancellation
  const handleCancelApplication = async () => {
    if (!application) return;
    
    setIsCancelling(true);
    setCancelError(null);
    
    const result = await cancelApplication(application.id);
    
    if (result.success) {
      // Refresh the page or navigate away
      navigate("/applications", { replace: true });
    } else {
      setCancelError(result.error || "Помилка при скасуванні заявки");
      setIsCancelling(false);
    }
  };

  useEffect(() => {
    async function loadApplication() {
      if (!id) return;
      setIsLoading(true);
      try {
        const data = await getApplication(id);
        if (data) {
          // If it's a draft, redirect to edit page
          if (data.status === "draft") {
            navigate(`/application/${id}`, { replace: true });
            return;
          }
          setApplication(data);
          
          // Load status history from database
          const history = await getStatusHistory(data.id);
          setStatusHistory(history);
          
          // Load application-phase document requests
          // Only load if status is in application phase or documents_requested
          const isInApplicationPhase = APPLICATION_PHASE_STATUSES.includes(data.status as ApplicationStatus);
          if (isInApplicationPhase) {
            const docRequests = await getDocumentRequests(data.id, "application");
            setDocumentRequests(docRequests);
          } else {
            // Even if past application phase, load requests but we won't show them as actionable
            const docRequests = await getDocumentRequests(data.id, "application");
            // Filter to only show fulfilled or cancelled requests (historical)
            const historicalRequests = docRequests.filter(r => r.status === "fulfilled" || r.status === "cancelled");
            setDocumentRequests(historicalRequests);
          }
          
          // Load report if application is in report-related status
          const reportStatuses = ["report_pending", "report_submitted", "report_review", "report_approved", "completed"];
          if (reportStatuses.includes(data.status)) {
            const reportData = await getApplicationReport(data.id);
            setReport(reportData);
            
            // Load report-phase document requests
            if (reportData) {
              const reportDocReqs = await getReportDocumentRequests(reportData.id);
              setReportDocumentRequests(reportDocReqs);
            } else {
              // No report yet, but there might be report-phase document requests for the application
              const reportDocReqs = await getDocumentRequests(data.id, "report");
              setReportDocumentRequests(reportDocReqs);
            }
          }
        } else {
          setError("Заявку не знайдено");
        }
      } catch {
        setError("Помилка завантаження заявки");
      } finally {
        setIsLoading(false);
      }
    }
    loadApplication();
  }, [id, navigate]);

  // Handle document upload for document requests
  const handleDocumentRequestUpload = async (request: DocumentRequestRecord, file: File) => {
    if (!application) return;
    
    setUploadingRequestId(request.id);
    setUploadSuccess(null);

    try {
      // 1. Upload file to Directus
      const formData = new FormData();
      formData.append("file", file);
      const uploadedFile = await directus.request(uploadFiles(formData));
      const fileData = uploadedFile as unknown as { id: string };

      // 2. Create application_documents record with document_request_id
      const docRecord = await directus.request(
        createItem("application_documents" as never, {
          application_id: application.id,
          document_type: `request_${request.id}`,
          file_id: fileData.id,
          file_name: file.name,
          document_request_id: request.id,
        } as never)
      );

      // 3. Fulfill the document request (docId is UUID string)
      const docId = (docRecord as unknown as { id: string }).id;
      await fulfillDocumentRequest(request.id, docId);

      // 4. Update local state
      setDocumentRequests(prev => 
        prev.map(r => r.id === request.id 
          ? { ...r, status: "fulfilled" as const, fulfilled_document_id: docId, fulfilled_at: new Date().toISOString() }
          : r
        )
      );

      setUploadSuccess(`Документ "${request.document_type}" успішно завантажено`);
    } catch (err) {
      console.error("Error uploading document:", err);
      setError("Помилка завантаження документа");
    } finally {
      setUploadingRequestId(null);
    }
  };

  if (isLoading) {
    return (
      <AppLayout userRole="applicant">
        <div className="w-full flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center justify-center">
            <Spinner size="lg" />
            <p className="mt-4 text-muted-foreground">Завантаження заявки...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !application) {
    return (
      <AppLayout userRole="applicant">
        <div className="w-full flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error || "Заявку не знайдено"}</p>
            <Button asChild variant="outline">
              <Link to="/applications">Повернутись до списку</Link>
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Use database history if available, otherwise compute from application data
  const computedHistory = getFullStatusHistory(application);
  const hasDbHistory = statusHistory.length > 0;
  const assistance = application.floors_count 
    ? calculateAssistance(application.floors_count, application.boiler_room_type)
    : null;

  return (
    <AppLayout userRole="applicant">
      <div className="space-y-6">
        {/* Back link */}
        <div className="flex items-center justify-between">
          <Link
            to="/applications"
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            До списку заявок
          </Link>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold tracking-tight">
                Заявка {application.application_number || `#${application.id}`}
              </h1>
              <span className={cn(
                "px-3 py-1 rounded-full text-[12px] font-medium",
                getStatusColor(application.status)
              )}>
                {APPLICATION_STATUS_LABELS[application.status] || application.status}
              </span>
            </div>
            <p className="text-[13px] text-muted-foreground mt-1">
              Створено: {formatDateTime(application.date_created)}
            </p>
          </div>
          
          {/* Cancel button - only show if cancellation is allowed */}
          {canUserCancelApplication(application.status as ApplicationStatus) && (
            <Button 
              variant="outline"
              onClick={() => setShowCancelDialog(true)}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
            >
              Скасувати заявку
            </Button>
          )}
        </div>

        {/* Cancel Confirmation Dialog */}
        {showCancelDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircleIcon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-[16px] font-semibold">Скасувати заявку?</h3>
              </div>
              
              <p className="text-[14px] text-muted-foreground mb-4">
                Ви впевнені, що хочете скасувати заявку <strong>№{application.application_number}</strong>? 
                Цю дію неможливо буде скасувати.
              </p>
              
              {cancelError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-[13px]">
                  {cancelError}
                </div>
              )}
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCancelDialog(false);
                    setCancelError(null);
                  }}
                  disabled={isCancelling}
                  className="flex-1"
                >
                  Ні, залишити
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleCancelApplication}
                  disabled={isCancelling}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {isCancelling ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Скасування...
                    </>
                  ) : (
                    "Так, скасувати"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Report Action Banner - shown when user needs to submit report */}
        {application.status === "report_pending" && (
          <div className="bg-amber-50 rounded-2xl border-[3px] border-amber-300 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-[16px] font-semibold text-amber-900">Час подати звіт!</h2>
                <p className="text-[13px] text-amber-700 mt-1">
                  Ви отримали кошти. Тепер необхідно подати звіт про придбання та встановлення обладнання.
                </p>
              </div>
              <Button 
                onClick={() => navigate(`/application/${application.id}/report`)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {report ? "Продовжити звіт" : "Створити звіт"}
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab("overview")}
            className={cn(
              "px-4 py-2 rounded-lg text-[13px] font-medium transition-colors",
              activeTab === "overview"
                ? "bg-white text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Огляд та статус
          </button>
          <button
            onClick={() => setActiveTab("details")}
            className={cn(
              "px-4 py-2 rounded-lg text-[13px] font-medium transition-colors",
              activeTab === "details"
                ? "bg-white text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Деталі заявки
          </button>
          {(report || application.status === "report_pending") && (
            <button
              onClick={() => setActiveTab("report")}
              className={cn(
                "px-4 py-2 rounded-lg text-[13px] font-medium transition-colors",
                activeTab === "report"
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Звіт
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Status Timeline from Database */}
            {hasDbHistory && (
              <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6">
                <h2 className="text-[15px] font-semibold mb-4">Історія змін статусу</h2>
                <div className="relative">
                  {statusHistory.map((item, index) => (
                    <div key={item.id} className="flex gap-4 pb-6 last:pb-0">
                      <div className="relative flex flex-col items-center">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                          item.new_status === "rejected" 
                            ? "bg-red-100" 
                            : item.new_status === "draft"
                              ? "bg-blue-100"
                              : "bg-green-100"
                        )}>
                          {item.new_status === "rejected" && <XCircleIcon className="w-5 h-5 text-red-600" />}
                          {item.new_status === "draft" && <PencilIcon className="w-4 h-4 text-blue-600" />}
                          {item.new_status !== "rejected" && item.new_status !== "draft" && (
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        {index < statusHistory.length - 1 && (
                          <div className="w-0.5 flex-1 mt-2 min-h-[16px] bg-green-200" />
                        )}
                      </div>
                      <div className="flex-1 pt-1">
                        <p className="text-[14px] font-medium">
                          {item.old_status && item.old_status !== "new" ? (
                            <>
                              {APPLICATION_STATUS_LABELS[item.old_status as ApplicationStatus] || item.old_status}
                              {" → "}
                            </>
                          ) : null}
                          {APPLICATION_STATUS_LABELS[item.new_status as ApplicationStatus] || item.new_status}
                        </p>
                        <p className="text-[12px] text-muted-foreground mt-0.5">
                          {formatDateTime(item.changed_at)}
                        </p>
                        {item.notes && (
                          <p className="text-[12px] text-muted-foreground/80 mt-0.5 bg-slate-50 px-2 py-1 rounded">
                            {item.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback: Computed Status Timeline (if no DB history) */}
            {!hasDbHistory && (
              <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6">
                <h2 className="text-[15px] font-semibold mb-4">Статус заявки</h2>
                <div className="relative">
                  {computedHistory.map((item, index) => (
                    <div key={`${item.status}-${index}`} className="flex gap-4 pb-6 last:pb-0">
                      <div className="relative flex flex-col items-center">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                          item.active 
                            ? item.icon === "error" 
                              ? "bg-red-100" 
                              : item.icon === "edit"
                                ? "bg-blue-100"
                                : "bg-green-100"
                            : "bg-slate-100"
                        )}>
                          {item.icon === "check" && <CheckCircleIcon className="w-5 h-5 text-green-600" />}
                          {item.icon === "clock" && <ClockIcon className="w-5 h-5 text-slate-400" />}
                          {item.icon === "error" && <XCircleIcon className="w-5 h-5 text-red-600" />}
                          {item.icon === "edit" && <PencilIcon className="w-4 h-4 text-blue-600" />}
                        </div>
                        {index < computedHistory.length - 1 && (
                          <div className={cn(
                            "w-0.5 flex-1 mt-2 min-h-[16px]",
                            item.active ? "bg-green-200" : "bg-slate-200"
                          )} />
                        )}
                      </div>
                      <div className="flex-1 pt-1">
                        <p className={cn(
                          "text-[14px] font-medium",
                          item.active ? "text-foreground" : "text-muted-foreground"
                        )}>
                          {item.label}
                        </p>
                        {item.date && (
                          <p className="text-[12px] text-muted-foreground mt-0.5">
                            {formatDateTime(item.date)}
                          </p>
                        )}
                        {item.description && (
                          <p className="text-[12px] text-muted-foreground/80 mt-0.5">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Document Requests (if any) - Only show during application phase */}
            {documentRequests.length > 0 && APPLICATION_PHASE_STATUSES.includes(application.status as ApplicationStatus) && (
              <div className="bg-orange-50 rounded-2xl border-[3px] border-orange-200 p-6">
                <h2 className="text-[15px] font-semibold text-orange-800 mb-4">Запити документів</h2>
                
                {uploadSuccess && (
                  <div className="mb-4 p-3 bg-green-100 border border-green-200 rounded-lg text-green-700 text-[12px]">
                    {uploadSuccess}
                  </div>
                )}
                
                <div className="space-y-3">
                  {documentRequests.map((req) => (
                    <div key={req.id} className="bg-white rounded-xl overflow-hidden border border-orange-100">
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-[13px] font-medium text-orange-900">{req.document_type}</p>
                            <p className="text-[12px] text-orange-700 mt-1">{req.description}</p>
                            <p className="text-[11px] text-orange-600 mt-2">
                              Запит від: {formatDateTime(req.requested_at)}
                              {req.deadline && <span> · Строк: {formatDate(req.deadline)}</span>}
                            </p>
                          </div>
                          <span className={cn(
                            "px-2 py-1 rounded-full text-[11px] font-medium shrink-0 ml-3",
                            req.status === "pending" && "bg-orange-100 text-orange-700",
                            req.status === "fulfilled" && "bg-green-100 text-green-700",
                            req.status === "overdue" && "bg-red-100 text-red-700",
                            req.status === "cancelled" && "bg-slate-100 text-slate-700"
                          )}>
                            {DOCUMENT_REQUEST_STATUS_LABELS[req.status]}
                          </span>
                        </div>
                      </div>
                      
                      {/* Upload section for pending/overdue requests */}
                      {(req.status === "pending" || req.status === "overdue") && (
                        <div className="px-4 pb-4">
                          {uploadingRequestId === req.id ? (
                            <div className="flex items-center justify-center p-4 border-2 border-dashed border-orange-300 rounded-xl bg-orange-50">
                              <Spinner size="sm" className="mr-2" />
                              <span className="text-[12px] text-orange-700">Завантаження...</span>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-orange-200 rounded-xl cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors">
                              <svg className="w-6 h-6 text-orange-500 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                              </svg>
                              <p className="text-[12px] font-medium text-orange-700">
                                Завантажити документ
                              </p>
                              <p className="text-[10px] text-orange-500 mt-0.5">PDF, JPG, PNG до 10 МБ</p>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleDocumentRequestUpload(req, file);
                                  e.target.value = "";
                                }}
                                className="hidden"
                              />
                            </label>
                          )}
                        </div>
                      )}
                      
                      {/* Show fulfilled info */}
                      {req.status === "fulfilled" && req.fulfilled_at && (
                        <div className="px-4 pb-4">
                          <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                            <CheckCircleIcon className="w-4 h-4 text-green-600" />
                            <span className="text-[11px] text-green-700">
                              Виконано {formatDateTime(req.fulfilled_at)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rejection reason */}
            {application.status === "rejected" && (
              <div className="bg-red-50 rounded-2xl border-[3px] border-red-200 p-6">
                <h2 className="text-[15px] font-semibold text-red-800 mb-2">Причина відхилення</h2>
                <p className="text-[14px] text-red-700">
                  {getRejectionReasonLabel(application.rejection_reason)}
                </p>
                {application.rejection_notes && (
                  <p className="text-[13px] text-red-600 mt-2">
                    {application.rejection_notes}
                  </p>
                )}
              </div>
            )}

            {/* Payment Info (if received) */}
            {(application.payment_amount || application.payment_date) && (
              <div className="bg-green-50 rounded-2xl border-[3px] border-green-200 p-6">
                <h2 className="text-[15px] font-semibold text-green-800 mb-4">Інформація про виплату</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {application.payment_amount && (
                    <DataField label="Сума виплати" value={formatMoney(application.payment_amount)} highlight />
                  )}
                  {application.payment_date && (
                    <DataField label="Дата виплати" value={formatDate(application.payment_date)} />
                  )}
                  {application.payment_received_at && (
                    <DataField label="Дата отримання" value={formatDate(application.payment_received_at)} />
                  )}
                  {application.order_number && (
                    <DataField label="Номер наказу" value={application.order_number} />
                  )}
                </div>
              </div>
            )}

            {/* Signature Info */}
            {application.signed_at && (
              <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6">
                <h2 className="text-[15px] font-semibold mb-4">Електронний підпис</h2>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  <span className="text-[14px]">
                    Заявку підписано КЕП/УЕП {formatDateTime(application.signed_at)}
                  </span>
                </div>
              </div>
            )}

            {/* Quick Summary */}
            <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6">
              <h2 className="text-[15px] font-semibold mb-4">Короткий огляд</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DataField label="Організація" value={application.organization_name} />
                <DataField label="Код ЄДРПОУ" value={application.edrpou_code} />
                <DataField label="Адреса будинку" value={application.building_address} className="md:col-span-2" />
                {assistance && (
                  <DataField label="Сума допомоги" value={formatMoney(assistance.amount)} highlight />
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "details" && (
          <div className="space-y-6">
            {/* Header - ЗАЯВА (same as StepReview) */}
            <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl border-[3px] border-primary/20 p-6 text-center">
              <h2 className="text-[16px] font-bold text-primary uppercase tracking-wide">
                ЗАЯВА
              </h2>
              <p className="text-[12px] text-primary/80 mt-2 max-w-lg mx-auto leading-relaxed">
                на отримання допомоги на придбання незалежних джерел електричної енергії 
                для забезпечення потреб співвласників багатоквартирних будинків
              </p>
              <div className="mt-4 text-[11px] text-muted-foreground">
                <p><span className="font-medium">Кому:</span> Міністерство розвитку громад та територій України</p>
                <p className="mt-1"><span className="font-medium">Від:</span> {application.organization_name || "_______________"}</p>
              </div>
            </div>

            {/* Section 1: Recipient (same style as StepReview) */}
            <ReadOnlySection title="Інформація про отримувача" step={1}>
              <div className="space-y-0">
                <DataRow
                  label="Форма управління"
                  value={application.recipient_type ? RECIPIENT_TYPE_LABELS[application.recipient_type as keyof typeof RECIPIENT_TYPE_LABELS] : null}
                />
                <DataRow label="Назва організації" value={application.organization_name} />
                <DataRow label="Код ЄДРПОУ" value={application.edrpou_code} mono />
                <DataRow label="Керівник" value={application.head_full_name} />
                <DataRow label="Юридична адреса" value={application.legal_address} />
                <DataRow label="Фактична адреса" value={application.actual_address} />
                <DataRow label="Телефон" value={application.phone} mono />
                <DataRow label="Email" value={application.email} />
                <DataRow label="IBAN" value={application.iban} mono />
              </div>
            </ReadOnlySection>

            {/* Section 2: Building (same style as StepReview) */}
            <ReadOnlySection title="Інформація про будинок" step={2}>
              <div className="space-y-0">
                <DataRow label="Адреса будинку" value={application.building_address} />
                <DataRow label="Кількість поверхів" value={application.floors_count} />
                <DataRow label="Наявність котельні" value={application.boiler_room_type ? BOILER_ROOM_LABELS[application.boiler_room_type] : "Немає"} />
                <DataRow label="Технічний паспорт" value={application.has_tech_passport ? "Є в наявності" : "Відсутній"} />
                <DataRow label="Кількість співвласників" value={application.co_owners_count} />
                <DataRow label="Кількість квартир" value={application.apartments_count} />
                <DataRow label="Загальна площа" value={application.total_area ? `${application.total_area} м²` : null} />
              </div>

              {assistance && (
                <div className="mt-4 p-3 bg-green-50 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[11px] text-green-600">Категорія допомоги</p>
                      <p className="text-[13px] font-semibold text-green-700">
                        {assistance.category === "category_a" ? "A" : assistance.category === "category_b" ? "B" : "C"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-green-600">Сума допомоги</p>
                      <p className="text-[16px] font-bold text-green-700">{formatMoneyLib(assistance.amount)}</p>
                    </div>
                  </div>
                </div>
              )}
            </ReadOnlySection>

            {/* Section 3: Equipment (same style as StepReview) */}
            <ReadOnlySection title="Обладнання" step={3}>
              <div className="space-y-3">
                <div>
                  <p className="text-[11px] text-muted-foreground mb-2">Обране обладнання:</p>
                  <div className="flex flex-wrap gap-2">
                    {application.equipment && application.equipment.length > 0 ? (
                      application.equipment.map((eq) => (
                        <span
                          key={eq.id}
                          className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-[12px] font-medium"
                        >
                          {EQUIPMENT_LABELS[eq.equipment_type as EquipmentType] || eq.equipment_type}
                        </span>
                      ))
                    ) : (
                      <span className="text-[12px] text-muted-foreground italic">Не обрано</span>
                    )}
                  </div>
                </div>

                <div className="pt-3 border-t border-border/30">
                  <p className="text-[11px] text-muted-foreground mb-2">Договір поставки:</p>
                  {application.has_supply_contract ? (
                    <>
                      <DataRow label="Номер договору" value={application.contract_number} />
                      <DataRow label="Дата договору" value={application.contract_date} />
                      <DataRow label="Постачальник" value={application.supplier_name} />
                      <DataRow label="ЄДРПОУ постачальника" value={application.supplier_edrpou} mono />
                      <DataRow
                        label="Вартість"
                        value={application.equipment_cost ? formatMoneyLib(application.equipment_cost) : null}
                      />
                      <DataRow label="Строк поставки" value={application.delivery_deadline} />
                    </>
                  ) : (
                    <p className="text-[13px] text-muted-foreground italic">
                      На даний момент договір поставки не укладено
                    </p>
                  )}
                </div>
              </div>
            </ReadOnlySection>

            {/* Section 4: Documents (same style as StepReview) */}
            <ReadOnlySection title="Документи" step={4}>
              <div className="space-y-2">
                {application.documents && application.documents.length > 0 ? (
                  application.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-green-50"
                    >
                      <div className="w-6 h-6 rounded-full flex items-center justify-center bg-green-500">
                        <CheckIconSmall className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[12px] text-green-700">
                        {DOCUMENT_LABELS[doc.document_type as DocumentType] || doc.document_type}
                        {doc.file_name && <span className="text-green-600 ml-1">({doc.file_name})</span>}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-[13px] text-muted-foreground italic">Документи не завантажено</p>
                )}
              </div>
            </ReadOnlySection>

            {/* Obligations (same style as StepReview - read-only) */}
            <div className="bg-amber-50 rounded-2xl border-[3px] border-amber-200 p-5">
              <h3 className="text-[15px] font-semibold text-amber-900 mb-4">Зобов'язання заявника</h3>
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    "w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 mt-0.5",
                    application.obligations_accepted ? "bg-primary border-primary" : "border-muted-foreground/30 bg-white"
                  )}
                >
                  {application.obligations_accepted && <CheckIconSmall className="w-4 h-4 text-primary-foreground" />}
                </div>
                <div className="flex-1">
                  <p className="text-[12px] text-amber-700 mb-3">
                    Підписанням заяви заявник підтверджує наступні зобов'язання:
                  </p>
                  <ul className="space-y-2 text-[12px] text-foreground/80 leading-relaxed">
                    {OBLIGATION_ITEMS.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>{item};</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Consents (same style as StepReview - read-only) */}
            <div className="bg-slate-50 rounded-2xl border-[3px] border-slate-200 p-5">
              <h3 className="text-[15px] font-semibold text-slate-900 mb-4">Згоди заявника</h3>
              <div className="space-y-3">
                <ReadOnlyObligation 
                  checked={application.consent_data_processing} 
                  text="Надаю згоду на обробку моїх персональних даних відповідно до Закону України «Про захист персональних даних» та Політики конфіденційності з метою реалізації експериментального проєкту щодо надання державної допомоги на придбання незалежних джерел електричної енергії."
                />
                <ReadOnlyObligation 
                  checked={application.consent_terms} 
                  text="Підтверджую, що ознайомлений(-а) з умовами реалізації експериментального проєкту та Порядком надання державної допомоги і зобов'язуюсь їх дотримуватися."
                />
              </div>
            </div>

            {/* Signature info (same style as StepReview) */}
            <div className="bg-blue-50 rounded-2xl border-[3px] border-blue-200 p-5">
              <h3 className="text-[15px] font-semibold text-blue-900 mb-2">Підписання заяви</h3>
              {application.signed_at ? (
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  <span className="text-[12px] text-blue-700">
                    Заявку підписано КЕП/УЕП {formatDateTime(application.signed_at)}
                  </span>
                </div>
              ) : (
                <p className="text-[12px] text-blue-700">
                  Заявку підписано та подано на розгляд
                </p>
              )}
            </div>
          </div>
        )}

        {/* Report Tab */}
        {activeTab === "report" && (
          <div className="space-y-6">
            {report ? (
              <>
                {/* Report Status Header */}
                <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-[15px] font-semibold">Звіт про виконання</h2>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "px-3 py-1 rounded-full text-[12px] font-medium",
                        report.status === "draft" && "bg-slate-100 text-slate-700",
                        report.status === "submitted" && "bg-blue-100 text-blue-700",
                        report.status === "review" && "bg-amber-100 text-amber-700",
                        report.status === "approved" && "bg-green-100 text-green-700",
                        report.status === "rejected" && "bg-red-100 text-red-700"
                      )}>
                        {REPORT_STATUS_LABELS[report.status as ReportStatus] || report.status}
                      </span>
                      {report.status === "draft" && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/application/${application.id}/report`)}
                        >
                          Редагувати
                        </Button>
                      )}
                    </div>
                  </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-[11px] text-muted-foreground">Отримано коштів</p>
                  <p className="text-[14px] font-semibold">{formatMoney(Number(report.amount_received))}</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Використано</p>
                  <p className="text-[14px] font-semibold">{formatMoney(Number(report.amount_used))}</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Власні кошти</p>
                  <p className="text-[14px] font-semibold">{formatMoney(Number(report.own_funds_used))}</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Залишок</p>
                  <p className="text-[14px] font-semibold">{formatMoney(Number(report.unused_funds))}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border/30 grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  {report.equipment_installed ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  ) : (
                    <ClockIcon className="w-5 h-5 text-amber-500" />
                  )}
                  <span className="text-[13px]">Обладнання встановлено</span>
                </div>
                <div className="flex items-center gap-2">
                  {report.equipment_tested ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  ) : (
                    <ClockIcon className="w-5 h-5 text-amber-500" />
                  )}
                  <span className="text-[13px]">Обладнання протестовано</span>
                </div>
                {report.installation_date && (
                  <div>
                    <p className="text-[11px] text-muted-foreground">Дата встановлення</p>
                    <p className="text-[13px]">{formatDate(report.installation_date)}</p>
                  </div>
                )}
              </div>

              {report.submitted_at && (
                <div className="mt-4 pt-4 border-t border-border/30">
                  <p className="text-[12px] text-muted-foreground">
                    Звіт подано: {formatDateTime(report.submitted_at)}
                  </p>
                </div>
              )}
            </div>

            {/* Report Equipment */}
            {report.equipment && report.equipment.length > 0 && (
              <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6">
                <h2 className="text-[15px] font-semibold mb-4">Придбане обладнання</h2>
                <div className="space-y-3">
                  {report.equipment.map((item, index) => (
                    <div key={item.id} className="p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-3 flex-1">
                          {/* Photo thumbnail */}
                          {item.photo_id && (
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 shrink-0">
                              <img 
                                src={`${import.meta.env.VITE_DIRECTUS_URL || 'http://63.177.81.222:8055'}/assets/${item.photo_id}?width=128&height=128&fit=cover`}
                                alt={`Фото ${item.name}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[12px] text-muted-foreground">#{index + 1}</span>
                              <span className="text-[13px] font-medium">{item.name}</span>
                            </div>
                            {item.serial_number && (
                              <p className="text-[11px] text-muted-foreground mt-1">
                                Серійний номер: <span className="font-mono">{item.serial_number}</span>
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[13px] font-semibold">{formatMoney(Number(item.price_with_vat))}</p>
                          {item.quantity > 1 && (
                            <p className="text-[11px] text-muted-foreground">
                              {item.quantity} шт. × {formatMoney(Number(item.price_per_unit))}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Total */}
                  <div className="flex justify-between items-center p-3 bg-primary/10 rounded-xl mt-2">
                    <span className="text-[13px] font-medium">Загальна вартість</span>
                    <span className="text-[15px] font-bold text-primary">
                      {formatMoney(report.equipment.reduce((sum, eq) => sum + Number(eq.price_with_vat || 0), 0))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Report Documents */}
            {report.documents && report.documents.length > 0 && (
              <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6">
                <h2 className="text-[15px] font-semibold mb-4">Документи звіту</h2>
                <div className="space-y-2">
                  {report.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-3 p-2 rounded-lg bg-green-50">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center bg-green-500">
                        <CheckIconSmall className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[12px] text-green-700">
                        {REPORT_DOCUMENT_LABELS[doc.document_type as ReportDocumentType] || doc.document_type}
                        {doc.file_name && <span className="text-green-600 ml-1">({doc.file_name})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Report Document Requests (if any) */}
            {reportDocumentRequests.length > 0 && (
              <div className="bg-orange-50 rounded-2xl border-[3px] border-orange-200 p-6">
                <h2 className="text-[15px] font-semibold text-orange-800 mb-4">Запити документів до звіту</h2>
                
                {uploadSuccess && (
                  <div className="mb-4 p-3 bg-green-100 border border-green-200 rounded-lg text-green-700 text-[12px]">
                    {uploadSuccess}
                  </div>
                )}
                
                <div className="space-y-3">
                  {reportDocumentRequests.map((req) => (
                    <div key={req.id} className="bg-white rounded-xl overflow-hidden border border-orange-100">
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-[13px] font-medium text-orange-900">{req.document_type}</p>
                            <p className="text-[12px] text-orange-700 mt-1">{req.description}</p>
                            <p className="text-[11px] text-orange-600 mt-2">
                              Запит від: {formatDateTime(req.requested_at)}
                              {req.deadline && <span> · Строк: {formatDate(req.deadline)}</span>}
                            </p>
                          </div>
                          <span className={cn(
                            "px-2 py-1 rounded-full text-[11px] font-medium shrink-0 ml-3",
                            req.status === "pending" && "bg-orange-100 text-orange-700",
                            req.status === "fulfilled" && "bg-green-100 text-green-700",
                            req.status === "overdue" && "bg-red-100 text-red-700",
                            req.status === "cancelled" && "bg-slate-100 text-slate-700"
                          )}>
                            {DOCUMENT_REQUEST_STATUS_LABELS[req.status]}
                          </span>
                        </div>
                      </div>
                      
                      {/* Upload section for pending/overdue requests */}
                      {(req.status === "pending" || req.status === "overdue") && (
                        <div className="px-4 pb-4">
                          {uploadingRequestId === req.id ? (
                            <div className="flex items-center justify-center p-4 border-2 border-dashed border-orange-300 rounded-xl bg-orange-50">
                              <Spinner size="sm" className="mr-2" />
                              <span className="text-[12px] text-orange-700">Завантаження...</span>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-orange-200 rounded-xl cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors">
                              <svg className="w-6 h-6 text-orange-500 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                              </svg>
                              <p className="text-[12px] font-medium text-orange-700">
                                Завантажити документ
                              </p>
                              <p className="text-[10px] text-orange-500 mt-0.5">PDF, JPG, PNG до 10 МБ</p>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleDocumentRequestUpload(req, file);
                                  e.target.value = "";
                                }}
                                className="hidden"
                              />
                            </label>
                          )}
                        </div>
                      )}
                      
                      {/* Show fulfilled info */}
                      {req.status === "fulfilled" && req.fulfilled_at && (
                        <div className="px-4 pb-4">
                          <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                            <CheckCircleIcon className="w-4 h-4 text-green-600" />
                            <span className="text-[11px] text-green-700">
                              Виконано {formatDateTime(req.fulfilled_at)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rejection reason if rejected */}
            {report.status === "rejected" && report.rejection_reason && (
              <div className="bg-red-50 rounded-2xl border-[3px] border-red-200 p-6">
                <h2 className="text-[15px] font-semibold text-red-800 mb-2">Причина відхилення звіту</h2>
                <p className="text-[14px] text-red-700">{getRejectionReasonLabel(report.rejection_reason)}</p>
              </div>
            )}

                {/* Notes */}
                {report.notes && (
                  <div className="bg-slate-50 rounded-2xl border-[3px] border-slate-200 p-6">
                    <h2 className="text-[15px] font-semibold text-slate-800 mb-2">Примітки</h2>
                    <p className="text-[14px] text-slate-700">{report.notes}</p>
                  </div>
                )}
              </>
            ) : (
              /* No report yet - show create report prompt */
              <div className="bg-white rounded-2xl border-[3px] border-border/50 p-8 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h2 className="text-[18px] font-semibold mb-2">Створіть звіт про виконання</h2>
                  <p className="text-[14px] text-muted-foreground mb-6">
                    Після отримання коштів та придбання обладнання, вам необхідно подати звіт з інформацією 
                    про придбане обладнання, його вартість та підтверджуючі документи.
                  </p>
                  
                  <div className="bg-slate-50 rounded-xl p-4 text-left mb-6">
                    <h3 className="text-[13px] font-semibold mb-3">Що потрібно для звіту:</h3>
                    <ul className="space-y-2 text-[12px] text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        Інформація про отримані та витрачені кошти
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        Список придбаного обладнання (назва, серійний номер, вартість)
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        Рішення зборів про затвердження звіту
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        Виписка з банківського рахунку
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        Накладні / акти приймання-передавання
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        Фото встановленого обладнання
                      </li>
                    </ul>
                  </div>
                  
                  <Button 
                    onClick={() => navigate(`/application/${application.id}/report`)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Створити звіт
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

// Helper component for displaying data fields (for overview tab)
function DataField({ 
  label, 
  value, 
  className,
  highlight 
}: { 
  label: string; 
  value: string | null | undefined; 
  className?: string;
  highlight?: boolean;
}) {
  return (
    <div className={className}>
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className={cn(
        "text-[14px]",
        highlight ? "font-semibold text-primary" : "text-foreground",
        !value && "text-muted-foreground"
      )}>
        {value || "—"}
      </p>
    </div>
  );
}

// Read-only section (matches StepReview Section but without edit button)
function ReadOnlySection({ title, step, children }: { title: string; step: number; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border-[3px] border-border/50 overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-border/30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <span className="text-[13px] font-semibold text-primary">{step}</span>
          </div>
          <h3 className="text-[14px] font-semibold">{title}</h3>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// DataRow (same as StepReview)
function DataRow({ label, value, mono }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
  return (
    <div className="flex justify-between py-2 border-b border-border/20 last:border-0">
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span className={cn("text-[12px] font-medium text-right max-w-[60%]", mono && "font-mono")}>
        {value || "—"}
      </span>
    </div>
  );
}

// Check icon for documents (smaller version)
function CheckIconSmall({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

// Read-only obligation (same style as StepReview but not interactive)
function ReadOnlyObligation({ checked, text }: { checked: boolean; text: string }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={cn(
          "w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 mt-0.5",
          checked ? "bg-primary border-primary" : "border-muted-foreground/30 bg-white"
        )}
      >
        {checked && <CheckIconSmall className="w-4 h-4 text-primary-foreground" />}
      </div>
      <span className="text-[12px] text-foreground/80 leading-relaxed">
        {text}
      </span>
    </div>
  );
}

export default ApplicationView;
